import React, { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "react-query";
import {
  FeedbackResponse,
  getGeneratedReply,
  getStoreList,
  getUnanswereFeedbacks,
  publishReplies,
  publishWBReplies,
  publishOzonReplies,
  Reply,
  WbFeedbackForApi,
  OzonFeedbackForApi,
  FeedbackForApi,
} from "../ApiService";
import FeedbackCard from "../components/FeedbackCard/FeedbackCard";
import Button from "../components/Button/Button";
import LoaderCircle from "../components/LoaderCircle/LoaderCircle";
import PageContainer from "../components/PageContainer/PageContainer";
import PageBlock from "../components/PageContainer/PageBlock";
import { useMarketplace } from "../contexts/MarketplaceContext";
import { isFeatureEnabled } from "../config/features";

type StoredReplies = Record<string, { text: string }>;

// Вспомогательная функция для получения массива со значениями заданного ключа
const makeArrayFromReplyObject = (object: StoredReplies) => {
  return Object.entries(object)
    .filter(([id, reply]) => reply.text.trim())
    .map(([id, reply]) => ({ id, ...reply }));
};

// Ключ для кэша ответов
const getRepliesCacheKey = (storeId: string) => [`feedback_replies_${storeId}`];

export default function FeedbackPage() {
  const { currentMarketplace, isWB } = useMarketplace();
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [allLoading, setAllLoading] = useState(false);
  const [loadingArray, setLoadingArray] = useState<string[]>([]);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasTextOnly, setHasTextOnly] = useState<boolean>(false);
  // Отключаем функциональность комментариев для Озона
  // const [includeComments, setIncludeComments] = useState<boolean>(false);
  const [showCachedReplies, setShowCachedReplies] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{current: number, total: number} | null>(null);
  const queryClient = useQueryClient();

  // Убираем принудительный перерендер - используем React Query для управления состоянием

  // Используем React Query для управления состоянием ответов
  const { data: replies = {}, isLoading: isRepliesLoading } = useQuery<StoredReplies>(
    selectedStoreId ? getRepliesCacheKey(selectedStoreId) : ['no_store_selected'],
    async () => {
      if (!selectedStoreId) return {};

      // Получаем ответы из localStorage
      const cachedData = localStorage.getItem(`feedback_replies_${selectedStoreId}`);
      console.log(`Loading replies from localStorage for store ${selectedStoreId}`);

      if (cachedData) {
        try {
          const parsedData = JSON.parse(cachedData);
          console.log(`Found ${Object.keys(parsedData).length} cached replies`);
          return parsedData;
        } catch (e) {
          console.error('Error parsing cached replies:', e);
        }
      }

      return {};
    },
    {
      enabled: !!selectedStoreId,
      staleTime: 5 * 60 * 1000, // Данные считаются свежими 5 минут
      cacheTime: 10 * 60 * 1000, // Кэш храним 10 минут
      refetchOnWindowFocus: false, // Не обновляем при фокусе окна
    }
  );

  // Мутация для обновления ответов
  const updateRepliesMutation = useMutation<StoredReplies, Error, { storeId: string, newReplies: StoredReplies }>(
    async ({ storeId, newReplies }) => {
      // Сохраняем в localStorage
      localStorage.setItem(`feedback_replies_${storeId}`, JSON.stringify(newReplies));
      console.log(`Saved ${Object.keys(newReplies).length} replies to localStorage for store ${storeId}`);
      return newReplies;
    },
    {
      onSuccess: (data, variables) => {
        // Обновляем кэш React Query
        queryClient.setQueryData(getRepliesCacheKey(variables.storeId), data);
        console.log(`Updated replies cache for store ${variables.storeId}`);

        // Инвалидируем кэш для принудительного обновления UI
        queryClient.invalidateQueries(getRepliesCacheKey(variables.storeId));
      }
    }
  );

  // Мутация для обновления одного ответа
  const updateSingleReplyMutation = useMutation<StoredReplies, Error, { storeId: string, feedbackId: string, text: string }>(
    async ({ storeId, feedbackId, text }) => {
      // Получаем текущие ответы
      const currentReplies = queryClient.getQueryData<StoredReplies>(getRepliesCacheKey(storeId)) || {};

      // Создаем обновленную копию
      const updatedReplies = {
        ...currentReplies,
        [feedbackId]: { text }
      };

      // Сохраняем в localStorage
      localStorage.setItem(`feedback_replies_${storeId}`, JSON.stringify(updatedReplies));
      console.log(`Saved updated reply for ${feedbackId} to localStorage`);

      return updatedReplies;
    },
    {
      onSuccess: (data, variables) => {
        // Обновляем кэш React Query
        queryClient.setQueryData(getRepliesCacheKey(variables.storeId), data);
        console.log(`Updated React Query cache for feedback ${variables.feedbackId}`);

        // Инвалидируем кэш для принудительного обновления UI
        queryClient.invalidateQueries(getRepliesCacheKey(variables.storeId));
      }
    }
  );

  const { data: stores } = useQuery(
    ["stores", currentMarketplace],
    () => getStoreList(currentMarketplace)
  );

  const {
    data: unansweredFeedback,
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useQuery<FeedbackResponse>(
    ["unansweredFeedback", selectedStoreId, perPage, currentPage, hasTextOnly, currentMarketplace],
    async () => {
      if (!selectedStoreId) {
        return {
          data: { countUnanswered: 0, countArchive: 0, feedbacks: [] },
          error: false,
          errorText: '',
          additionalErrors: null
        };
      }

      // Для Ozon загружаем все неотвеченные отзывы, для WB используем пагинацию
      if (!isWB) {
        // Для Ozon всегда загружаем все неотвеченные отзывы
        return await getUnanswereFeedbacks({
          page: 1,
          perPage: 1000, // Большое число для получения всех отзывов
          storeId: selectedStoreId,
          hasTextOnly: hasTextOnly,
          includeComments: false, // Отключено для Озона
        });
      } else {
        // Для WB используем обычную пагинацию
        return await getUnanswereFeedbacks({
          page: currentPage,
          perPage: perPage,
          storeId: selectedStoreId,
          hasTextOnly: hasTextOnly,
          includeComments: false, // Отключено для Озона
        });
      }
    },
    {
      enabled: !!selectedStoreId,
      // Добавляем настройки для уменьшения количества перезапросов
      staleTime: 30000, // Данные считаются свежими в течение 30 секунд
      cacheTime: 300000, // Кэшируем данные на 5 минут
      refetchOnWindowFocus: false, // Отключаем автоматическое обновление при фокусе окна
      refetchOnMount: false, // Отключаем автоматическое обновление при монтировании
    }
  );

  const allFeedbacks = unansweredFeedback?.data.feedbacks || [];

  // Логика пагинации: для Ozon применяем пагинацию на фронте, для WB используем серверную
  const feedbackList = !isWB
    ? allFeedbacks.slice((currentPage - 1) * perPage, currentPage * perPage)
    : allFeedbacks;

  // Отображаем общее количество отзывов
  // Для Ozon используем общее количество загруженных отзывов, для WB - данные с сервера
  const totalFeedbacks = !isWB
    ? allFeedbacks.length // Общее количество всех загруженных отзывов
    : (unansweredFeedback?.data.countUnanswered || 0);

  const totalPages = totalFeedbacks ? Math.ceil(totalFeedbacks / perPage) : 1;

  const marketplaceName = isWB ? "Wildberries" : "Ozon";

  const handleStoreChange = (event: any) => {
    setSelectedStoreId(event.target.value);
  };

  // Мемоизируем функцию обработки изменения ответа
  const handleReplyChange = useCallback(
    (id: string, text: string) => {
      console.log(`Updating reply for ${id}:`, {
        newText: text.substring(0, 30) + (text.length > 30 ? '...' : ''),
        length: text.length,
        type: typeof text
      });

      if (selectedStoreId) {
        // Проверяем, что text - это строка
        if (typeof text !== 'string') {
          console.error(`Invalid text type for ${id}:`, typeof text);
          return;
        }

        updateSingleReplyMutation.mutate({
          storeId: selectedStoreId,
          feedbackId: id,
          text
        });
      }
    },
    [selectedStoreId, updateSingleReplyMutation]
  );

  const handleGenerateOne = useCallback(
    async (id: string) => {
      if (feedbackList && selectedStoreId) {
        try {
          setLoadingArray((prevLoading) => {
            if (!prevLoading.includes(id)) {
              return [...prevLoading, id];
            }
            return prevLoading;
          });

          const feedback = feedbackList.find((feedback) => feedback.id === id)!;

          // Создаем объект с порядковым номером для API
          const feedbackForApi = isWB ? {
            id: "1", // Всегда используем "1" для единичного запроса
            text: feedback.text || '',
            pros: feedback.pros || '',
            cons: feedback.cons || '',
            customerName: feedback.userName || '',
            productValuation: feedback.productValuation || 0,
            productName: feedback.productDetails?.productName || '',
            marketplace: 'wb' as const,
          } : {
            id: "1", // Всегда используем "1" для единичного запроса
            text: feedback.text || '',
            customerName: feedback.author || '',
            productValuation: feedback.rating || 0,
            marketplace: 'ozon' as const,
          };

          console.log(`Generating reply for feedback ${id} with API ID ${feedbackForApi.id}`);

          // Сначала очищаем текущий ответ
          const currentReplies = queryClient.getQueryData<StoredReplies>(getRepliesCacheKey(selectedStoreId)) || {};
          const updatedRepliesCleared = { ...currentReplies };

          // Если есть текущий ответ, сначала удаляем его
          if (updatedRepliesCleared[id]) {
            delete updatedRepliesCleared[id];

            // Обновляем кэш с очищенным ответом
            await updateRepliesMutation.mutateAsync({
              storeId: selectedStoreId,
              newReplies: updatedRepliesCleared
            });

            console.log(`Cleared existing reply for ${id}`);

            // Небольшая задержка для перерендера
            await new Promise(resolve => setTimeout(resolve, 50));
          }

          const response = await getGeneratedReply({
            feedbacks: [feedbackForApi],
            storeId: selectedStoreId,
          });

          // Обновляем состояние replies с новым ответом
          if (response && response.length > 0) {
            // Берем первый ответ, так как мы отправляли только один отзыв
            const newReply = response[0];
            console.log(`Received reply for API ID ${newReply.id}:`, newReply.text.substring(0, 30));

            // Проверяем, разрешен ли ответ
            if (newReply.isAllowed !== false) {
              // Получаем текущие ответы из кэша (после очистки)
              const currentRepliesAfterClear = queryClient.getQueryData<StoredReplies>(getRepliesCacheKey(selectedStoreId)) || {};

              // Создаем обновленную копию replies
              // Используем реальный ID отзыва (id), а не ID из ответа API (newReply.id)
              const updatedReplies = {
                ...currentRepliesAfterClear,
                [id]: { text: newReply.text }
              };

              // Обновляем кэш и localStorage через мутацию
              await updateRepliesMutation.mutateAsync({
                storeId: selectedStoreId,
                newReplies: updatedReplies
              });

              console.log(`Set new reply for ${id} from API response with ID ${newReply.id}`);
            } else {
              console.warn(`Reply for ${id} was not allowed`);
            }
          }

          // Снимаем флаг загрузки с небольшой задержкой
          setTimeout(() => {
            setLoadingArray((prevLoading) =>
              prevLoading.filter((value) => value !== id)
            );
            console.log(`Loading flag reset for ${id}`);
          }, 50);
        } catch (error) {
          console.error("Generate reply error:", error);
          setLoadingArray((prevLoading) =>
            prevLoading.filter((value) => value !== id)
          );
        }
      }
    },
    [feedbackList, selectedStoreId, isWB, queryClient, updateRepliesMutation]
  );

  const handleGenerateAll = async () => {
    if (allFeedbacks && selectedStoreId) {
      const idArray = allFeedbacks.map((feedback) => feedback.id);
      setAllLoading(true);
      setLoadingArray(idArray);

      try {
        const BATCH_SIZE = 10; // Размер батча
        const totalBatches = Math.ceil(allFeedbacks.length / BATCH_SIZE);
        const allProcessedReplies: StoredReplies = {};

        console.log(`Starting batch generation: ${allFeedbacks.length} feedbacks, ${totalBatches} batches`);

        // Обрабатываем отзывы батчами последовательно
        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
          // Обновляем прогресс
          setBatchProgress({ current: batchIndex + 1, total: totalBatches });

          const startIdx = batchIndex * BATCH_SIZE;
          const endIdx = Math.min(startIdx + BATCH_SIZE, allFeedbacks.length);
          const batchFeedbacks = allFeedbacks.slice(startIdx, endIdx);

          console.log(`Processing batch ${batchIndex + 1}/${totalBatches}: feedbacks ${startIdx + 1}-${endIdx}`);

          // Создаем карту для текущего батча
          const batchIdMap: Map<string, string> = new Map();

          // Подготавливаем отзывы текущего батча
          const preparedBatchFeedbacks = batchFeedbacks.map((feedback, index) => {
            const temporaryId = String(index + 1); // ID от 1 до 10 в каждом батче
            const feedbackForApi = isWB ? {
              id: temporaryId,
              text: feedback.text || '',
              pros: feedback.pros || '',
              cons: feedback.cons || '',
              customerName: feedback.userName || '',
              productValuation: feedback.productValuation || 0,
              productName: feedback.productDetails?.productName || '',
              marketplace: 'wb' as const,
            } : {
              id: temporaryId,
              text: feedback.text || '',
              customerName: feedback.author || '',
              productValuation: feedback.rating || 0,
              marketplace: 'ozon' as const,
            };

            // Сохраняем соответствие временного ID и реального ID
            batchIdMap.set(temporaryId, feedback.id);
            return feedbackForApi;
          });

          console.log(`Sending batch ${batchIndex + 1} with ${preparedBatchFeedbacks.length} feedbacks`);

          // Отправляем запрос на генерацию для текущего батча
          const batchResponse = await getGeneratedReply({
            feedbacks: preparedBatchFeedbacks,
            storeId: selectedStoreId,
          });

          console.log(`Received ${batchResponse?.length || 0} replies for batch ${batchIndex + 1}`);

          // Обрабатываем ответы текущего батча
          if (batchResponse && batchResponse.length > 0) {
            batchResponse.forEach(reply => {
              if (reply.id && reply.text && reply.isAllowed !== false) {
                const realId = batchIdMap.get(reply.id);
                if (realId) {
                  allProcessedReplies[realId] = { text: reply.text };
                  console.log(`Batch ${batchIndex + 1}: Added reply for ${realId} (temp ID: ${reply.id})`);
                } else {
                  console.warn(`Batch ${batchIndex + 1}: No real ID found for temp ID: ${reply.id}`);
                }
              }
            });

            // Обновляем состояние после каждого батча
            await updateRepliesMutation.mutateAsync({
              storeId: selectedStoreId,
              newReplies: { ...allProcessedReplies }
            });

            console.log(`Batch ${batchIndex + 1} completed. Total replies so far: ${Object.keys(allProcessedReplies).length}`);
          }

          // Небольшая задержка между батчами для избежания перегрузки
          if (batchIndex < totalBatches - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }

        console.log(`All batches completed. Total replies: ${Object.keys(allProcessedReplies).length} / ${allFeedbacks.length}`);

      } catch (error) {
        console.error("Generate all replies error:", error);
        const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
        alert(`Ошибка при генерации: ${errorMessage}`);
      } finally {
        setAllLoading(false);
        setLoadingArray([]);
        setBatchProgress(null);
      }
    }
  };

  // Загрузка кэшированных ответов при монтировании
  useEffect(() => {
    if (selectedStoreId) {
      // Загружаем ответы из кэша через React Query
      queryClient.invalidateQueries(getRepliesCacheKey(selectedStoreId));
    }
  }, [selectedStoreId, queryClient]);

  useEffect(() => {
    if (stores?.length) {
      setSelectedStoreId(stores[0].id);
    }
  }, [stores]);

  // Обработка изменения магазина
  useEffect(() => {
    if (selectedStoreId) {
      // Сбрасываем страницу при изменении магазина
      setCurrentPage(1);

      // Загружаем ответы из кэша
      queryClient.invalidateQueries(getRepliesCacheKey(selectedStoreId));
    }
  }, [selectedStoreId, queryClient]);

  // Сброс страницы при изменении параметров
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStoreId, perPage]);

  // Сброс состояния при смене маркетплейса
  useEffect(() => {
    setSelectedStoreId(null);
    setCurrentPage(1);
  }, [currentMarketplace]);

  // Получаем список отзывов для отображения
  const displayedFeedbacks = feedbackList;

  const hasFeedbacks = displayedFeedbacks && displayedFeedbacks.length > 0;

  // Обработка изменения страницы пагинации
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Обработка изменения фильтров
  const handleFilterChange = (field: string, value: boolean) => {
    if (field === "hasTextOnly") {
      setHasTextOnly(value);
    } else if (field === "showCachedReplies") {
      setShowCachedReplies(value);
    }
    setCurrentPage(1); // Сбрасываем страницу при изменении фильтров
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    return (
      <div className="flex items-center gap-2 justify-center mt-4">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Назад
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => handlePageChange(1)}
              className="px-3 py-1 rounded border"
            >
              1
            </button>
            {startPage > 2 && <span>...</span>}
          </>
        )}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`px-3 py-1 rounded border ${page === currentPage
              ? 'bg-blue-500 text-white'
              : 'hover:bg-gray-100'
              }`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span>...</span>}
            <button
              onClick={() => handlePageChange(totalPages)}
              className="px-3 py-1 rounded border"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Вперед
        </button>

        <span className="ml-4 text-sm text-gray-600">
          Страница {currentPage} из {totalPages}
        </span>
      </div>
    );
  };

  // Обработка отправки всех ответов
  const handleSendAllReplies = async () => {
    if (selectedStoreId && Object.keys(replies).length > 0) {
      setAllLoading(true);

      try {
        const replyArray = makeArrayFromReplyObject(replies);
        console.log(`Sending ${replyArray.length} replies`);

        if (isWB) {
          await publishWBReplies({
            storeId: selectedStoreId,
            replies: replyArray,
          });
        } else {
          await publishOzonReplies({
            storeId: selectedStoreId,
            replies: replyArray,
          });
        }

        console.log('All replies sent successfully');

        // Очищаем кэш ответов
        updateRepliesMutation.mutate({
          storeId: selectedStoreId,
          newReplies: {}
        });

        // Обновляем список отзывов
        await refetch();
      } catch (error) {
        console.error("Send all replies error:", error);
        alert("Ошибка при отправке ответов");
      } finally {
        setAllLoading(false);
        setLoadingArray([]);
      }
    }
  };

  const handleSendReply = useCallback(
    async (id: string) => {
      if (selectedStoreId && replies[id]?.text) {
        try {
          setLoadingArray((prevLoading) => {
            if (!prevLoading.includes(id)) {
              return [...prevLoading, id];
            }
            return prevLoading;
          });

          let response;
          if (isWB) {
            response = await publishWBReplies({
              storeId: selectedStoreId,
              replies: [
                {
                  id,
                  text: replies[id].text,
                },
              ],
            });
          } else {
            response = await publishOzonReplies({
              storeId: selectedStoreId,
              replies: [
                {
                  id,
                  text: replies[id].text,
                },
              ],
            });
          }

          if (response) {
            console.log(`Reply for ${id} sent successfully`);

            // Получаем текущие ответы из кэша
            const currentReplies = queryClient.getQueryData<StoredReplies>(getRepliesCacheKey(selectedStoreId)) || {};

            // Удаляем отправленный ответ из кэша
            const updatedReplies = { ...currentReplies };
            delete updatedReplies[id];

            // Обновляем кэш и localStorage через мутацию
            updateRepliesMutation.mutate({
              storeId: selectedStoreId,
              newReplies: updatedReplies
            });

            // Обновляем список отзывов
            await refetch();
          }

          setLoadingArray((prevLoading) =>
            prevLoading.filter((value) => value !== id)
          );
        } catch (error) {
          console.error("Send reply error:", error);
          setLoadingArray((prevLoading) =>
            prevLoading.filter((value) => value !== id)
          );
        }
      }
    },
    [replies, selectedStoreId, refetch, isWB, queryClient, updateRepliesMutation]
  );

  return (
    <PageContainer>
      {(isLoading || isRefetching || allLoading) && <LoaderCircle />}

      {/* Индикатор прогресса батчей */}
      {batchProgress && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 1000,
          minWidth: '300px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px' }}>
            Генерация ответов
          </div>
          <div style={{ fontSize: '16px', marginBottom: '15px' }}>
            Батч {batchProgress.current} из {batchProgress.total}
          </div>
          <div style={{ 
            width: '100%', 
            height: '10px', 
            backgroundColor: '#e0e0e0', 
            borderRadius: '5px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(batchProgress.current / batchProgress.total) * 100}%`,
              height: '100%',
              backgroundColor: '#4CAF50',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{ fontSize: '14px', marginTop: '10px', color: '#666' }}>
            {Math.round((batchProgress.current / batchProgress.total) * 100)}%
          </div>
        </div>
      )}

      {/* Затемнение фона при генерации */}
      {batchProgress && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 999
        }} />
      )}

      {/* Отладочная информация */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '12px' }}>
          <details>
            <summary style={{ fontWeight: 'bold', cursor: 'pointer' }}>Debug: Reply State</summary>
            <pre>{JSON.stringify({
              repliesCount: Object.keys(replies).length,
              repliesFirst3Keys: Object.keys(replies).slice(0, 3),
              repliesFirst3: Object.entries(replies).slice(0, 3).map(([id, r]) => ({ id, textLength: r.text?.length || 0 }))
            }, null, 2)}</pre>
          </details>
        </div>
      )}

      <PageBlock>
        <h3 className="h2Font mb-4">Отзывы {marketplaceName}</h3>

        {/* Блок управления с улучшенной вёрсткой */}
        <div className="flex flex-col gap-6">
          {/* Верхний ряд: выбор магазина и кнопки действий */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <select
              value={selectedStoreId || ""}
              onChange={handleStoreChange}
              className="p-2 border rounded w-full md:w-auto min-w-[200px]"
            >
              <option value="" disabled>
                Выберите магазин
              </option>
              {stores?.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>

            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <Button
                title="Сгенерировать все"
                status={
                  allLoading || loadingArray.length > 0 || !allFeedbacks?.length || isLoading || isRefetching
                    ? "disabled"
                    : "enabled"
                }
                onClick={handleGenerateAll}
              />
              <Button
                title="Отправить все"
                status={
                  allLoading || makeArrayFromReplyObject(replies).length === 0 || isLoading || isRefetching
                    ? "disabled"
                    : "enabled"
                }
                onClick={handleSendAllReplies}
              />
            </div>
          </div>

          {/* Средний ряд: чекбоксы фильтрации */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hasTextOnly"
                checked={hasTextOnly}
                onChange={(e) => handleFilterChange("hasTextOnly", e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="hasTextOnly" className="whitespace-nowrap">Только с текстом</label>
            </div>

            {/* Функциональность "С комментариями" отключена для Озона */}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showCachedReplies"
                checked={showCachedReplies}
                onChange={(e) => handleFilterChange("showCachedReplies", e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="showCachedReplies" className="whitespace-nowrap">Кэшированные ответы</label>
            </div>
          </div>

          {/* Нижний ряд: выбор количества отзывов и счётчик */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <select
              className="p-2 border rounded w-full md:w-auto min-w-[200px]"
              onChange={(event) => setPerPage(parseInt(event.target.value))}
              value={perPage}
            >
              <option value={0} disabled>
                Количество отзывов
              </option>
              {[5, 10, 15, 20, 25, 50, 100].map((count, index) => (
                <option key={index} value={count}>
                  {count}
                </option>
              ))}
            </select>

            {feedbackList && (
              <div className="bodyBoldFont">
                Отзывов на странице: {feedbackList.length} из {totalFeedbacks || 0}
              </div>
            )}
          </div>

          {/* Пагинация */}
          <div className="mt-2">
            {renderPagination()}
          </div>

          {isError && <div className="h3Font mt-4">Магазин не настроен</div>}
        </div>
      </PageBlock>

      {/* Отображаем отзывы только если они есть */}
      {displayedFeedbacks?.length > 0 ? (
        displayedFeedbacks?.map((feedback, index) => {
          // Логируем информацию о карточке отзыва и её ответе
          const currentReply = replies[feedback.id]?.text || "";

          // Подробное логирование для отладки
          console.log(`------- Feedback ${feedback.id} Debug Info -------`);
          console.log(`Raw reply data:`, replies[feedback.id]);
          console.log(`All replies keys (${Object.keys(replies).length}):`, Object.keys(replies));
          console.log(`Has reply in state:`, Object.keys(replies).includes(feedback.id));
          console.log(`Reply value to pass:`, currentReply);
          console.log(`Reply in state type:`, typeof replies[feedback.id]);
          console.log(`Reply text type:`, typeof replies[feedback.id]?.text);
          console.log(`React Query cache state updated`);

          console.log(`Rendering ${isWB ? 'WB' : 'OZON'} feedback card ${index + 1}/${displayedFeedbacks.length} (ID: ${feedback.id}):`, {
            hasReply: !!replies[feedback.id],
            replyText: currentReply.substring(0, 30) + (currentReply.length > 30 ? '...' : ''),
            textLength: currentReply.length,
            feedbackData: {
              id: feedback.id,
              marketplace: isWB ? 'wb' : 'ozon',
              product: isWB ?
                (feedback as any).productDetails?.productName || feedback.productName :
                feedback.productName,
              canAnswer: feedback.canAnswer
            }
          });

          // Для Озона добавим дополнительную проверку свойств
          if (!isWB) {
            console.log(`OZON feedback ${feedback.id} details:`, {
              replyProps: replies[feedback.id],
              replyKeysCount: Object.keys(replies).length,
              replyInState: JSON.stringify(replies[feedback.id]),
              containsId: Object.keys(replies).includes(feedback.id)
            });
          }

          return (
            <FeedbackCard
              key={feedback.id}
              feedback={feedback}
              replyValue={currentReply}
              isLoading={loadingArray.includes(feedback.id)}
              onReplyChange={handleReplyChange}
              onSendReply={handleSendReply}
              onGenerateReply={handleGenerateOne}
            />
          );
        })
      ) : (
        !isLoading && !isRefetching && (
          <div className="flex flex-col items-center justify-center p-8 bg-white rounded-xl shadow-md">
            <p className="text-lg text-gray-600">
              {hasTextOnly
                ? "Нет отзывов с текстом"
                : "Нет отзывов"}
            </p>
          </div>
        )
      )}
    </PageContainer>
  );
}
